import React from 'react';
import { Box, Button, Chip, Collapse, Stack, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { DashaEntry, RuntimeDashaResult, formatDashaDate, formatDashaTime } from '../../utils/dashaCalculator';

type Props = {
  timeline: RuntimeDashaResult;
  selected: { md: DashaEntry; ad: DashaEntry; pd: DashaEntry; sookshma: DashaEntry; prana: DashaEntry };
  current: { md?: DashaEntry | null; ad?: DashaEntry | null; pd?: DashaEntry | null; sookshma?: DashaEntry | null; prana?: DashaEntry | null };
  children: { sookshma: DashaEntry[]; prana: DashaEntry[] };
  labels: { md: string; ad: string; pd: string; sookshma: string; prana: string; current: string };
  planet: (entry: DashaEntry) => string;
  onSelect: { md: (entry: DashaEntry) => void; ad: (entry: DashaEntry) => void; pd: (entry: DashaEntry) => void; sookshma: (entry: DashaEntry) => void; prana: (entry: DashaEntry) => void };
};

const keyOf = (entry?: DashaEntry | null) => entry ? `${entry.lord}-${entry.start.toISOString()}` : '';

export default function CollapsibleDashaTimeline({ timeline, selected, current, children, labels, planet, onSelect }: Props) {
  const [openMd, setOpenMd] = React.useState(keyOf(selected.md));
  const [openAd, setOpenAd] = React.useState(keyOf(selected.ad));
  const [openPd, setOpenPd] = React.useState(keyOf(selected.pd));
  const [openSookshma, setOpenSookshma] = React.useState(keyOf(selected.sookshma));
  const tz = timeline.timezoneMinutes || 0;

  React.useEffect(() => setOpenMd(keyOf(selected.md)), [selected.md]);
  React.useEffect(() => setOpenAd(keyOf(selected.ad)), [selected.ad]);
  React.useEffect(() => setOpenPd(keyOf(selected.pd)), [selected.pd]);
  React.useEffect(() => setOpenSookshma(keyOf(selected.sookshma)), [selected.sookshma]);

  const toggle = (entry: DashaEntry, openKey: string, setOpen: (key: string) => void, select: (entry: DashaEntry) => void, reset?: () => void) => {
    const key = keyOf(entry);
    if (key === openKey) return setOpen('');
    select(entry);
    reset?.();
    setOpen(key);
  };
  const range = (entry: DashaEntry, includeTime = false) => includeTime
    ? `${formatDashaDate(entry.start, tz)} ${formatDashaTime(entry.start, tz)} – ${formatDashaDate(entry.end, tz)} ${formatDashaTime(entry.end, tz)}`
    : `${formatDashaDate(entry.start, tz)} – ${formatDashaDate(entry.end, tz)}`;

  const row = (entry: DashaEntry, levelLabel: string, depth: number, open: boolean, expandable: boolean, active: boolean, onClick: () => void, isCurrent: boolean) => (
    <Button
      key={keyOf(entry)}
      onClick={onClick}
      variant={active ? 'contained' : 'outlined'}
      color={isCurrent ? 'success' : 'primary'}
      fullWidth
      sx={{ justifyContent:'space-between', textAlign:'left', textTransform:'none', py:0.8, px:1.1, width:'100%', ...(!active && { bgcolor:'background.paper', color:'text.primary', borderColor:'divider', '&:hover': { bgcolor:'action.hover', borderColor:isCurrent ? 'success.main' : 'primary.main' } }) }}
    >
      <Box component="span" sx={{ display:'flex', alignItems:'center', gap:0.75, minWidth:0 }}>
        {expandable ? (open ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>) : <Box sx={{ width:20 }}/>} 
        <Box component="span" sx={{ fontWeight:800 }}>{planet(entry)}</Box>
        <Box component="span" sx={{ fontSize:11 }}>{levelLabel}</Box>
        {isCurrent && <Chip label={labels.current} size="small" color="success" sx={{ height:20 }}/>} 
      </Box>
      <Box component="span" sx={{ fontSize:11, ml:1, whiteSpace:'nowrap' }}>{range(entry, depth >= 3)}</Box>
    </Button>
  );

  return <Stack spacing={0.7}>
    {timeline.mahadashas.map((md) => {
      const mdOpen = openMd === keyOf(md);
      return <Box key={keyOf(md)}>
        {row(md, labels.md, 0, mdOpen, true, keyOf(md) === keyOf(selected.md), () => toggle(md, openMd, setOpenMd, onSelect.md, () => { setOpenAd(''); setOpenPd(''); setOpenSookshma(''); }), keyOf(md) === keyOf(current.md))}
        <Collapse in={mdOpen} unmountOnExit><Stack spacing={0.65} sx={{ mt:0.65, pl:{ xs:1, sm:3 } }}>
          {md.antardashas?.map((ad) => {
            const adOpen = openAd === keyOf(ad);
            return <Box key={keyOf(ad)}>
              {row(ad, labels.ad, 1, adOpen, true, keyOf(ad) === keyOf(selected.ad), () => toggle(ad, openAd, setOpenAd, onSelect.ad, () => { setOpenPd(''); setOpenSookshma(''); }), keyOf(ad) === keyOf(current.ad))}
              <Collapse in={adOpen} unmountOnExit><Stack spacing={0.6} sx={{ mt:0.6, pl:{ xs:1, sm:3 } }}>
                {ad.pratyantardashas?.map((pd) => {
                  const pdOpen = openPd === keyOf(pd);
                  return <Box key={keyOf(pd)}>
                    {row(pd, labels.pd, 2, pdOpen, true, keyOf(pd) === keyOf(selected.pd), () => toggle(pd, openPd, setOpenPd, onSelect.pd, () => setOpenSookshma('')), keyOf(pd) === keyOf(current.pd))}
                    <Collapse in={pdOpen} unmountOnExit><Stack spacing={0.55} sx={{ mt:0.55, pl:{ xs:1, sm:3 } }}>
                      {keyOf(pd) === keyOf(selected.pd) && children.sookshma.map((sd) => {
                        const sdOpen = openSookshma === keyOf(sd);
                        return <Box key={keyOf(sd)}>
                          {row(sd, labels.sookshma, 3, sdOpen, true, keyOf(sd) === keyOf(selected.sookshma), () => toggle(sd, openSookshma, setOpenSookshma, onSelect.sookshma), keyOf(sd) === keyOf(current.sookshma))}
                          <Collapse in={sdOpen} unmountOnExit><Stack spacing={0.5} sx={{ mt:0.5, pl:{ xs:1, sm:3 } }}>
                            {keyOf(sd) === keyOf(selected.sookshma) && children.prana.map((prana) => (
                              <Box key={keyOf(prana)}>
                                {row(prana, labels.prana, 4, false, false, keyOf(prana) === keyOf(selected.prana), () => onSelect.prana(prana), keyOf(prana) === keyOf(current.prana))}
                              </Box>
                            ))}
                          </Stack></Collapse>
                        </Box>;
                      })}
                    </Stack></Collapse>
                  </Box>;
                })}
              </Stack></Collapse>
            </Box>;
          })}
        </Stack></Collapse>
      </Box>;
    })}
    <Typography variant="caption" color="text.secondary" sx={{ pt:0.5 }}>Select a period to expand the next Dasha level.</Typography>
  </Stack>;
}
